import React, { useEffect, createContext, useReducer, useState } from 'react';

import { useTranslation, initReactI18next } from 'react-i18next';
import './i18n';

import { appReducer, StateType, ActionType } from './Reducer';

import Paper from '@material-ui/core/Paper';

import AppHeader from './components/organisms/AppHeader';
import AppFooter from './components/organisms/AppFooter';
import MenuTab from './components/organisms/MenuTab';
import HomePage from './components/Pages/Home/HomePage';
import TrendGraphPage from './components/Pages/TrendGraph/TrendGraphPage';
import TrendGraphGroupSelectPage from './components/Pages/TrendGraph/TrendGraphGroupSelectPage';
import RealTimeTrendGraphPage from './components/Pages/RealTimeTrendGraph/RealTimeTrendGraphPage';
import RealTimeTrendGraphGroupSelectPage from './components/Pages/RealTimeTrendGraph/RealTimeTrendGraphGroupSelectPage';
import PhaseCurrentPage from './components/Pages/PhaseCurrent/PhaseCurrentPage';
import PhaseCurrentGroupSelectPage from './components/Pages/PhaseCurrent/PhaseCurrentGroupSelectPage';
import RawWaveformComparisonPage from './components/Pages/RawWaveformComparison/RawWaveformComparisonPage';
import RawWaveformComparisonGroupSelectPage from './components/Pages/RawWaveformComparison/RawWaveformComparisonGroupSelectPage';
import RawWaveformComparisonConanairGroupSelectPage from './components/Pages/RawWaveformComparisonConanair/RawWaveformComparisonConanairGroupSelectPage';
import RawWaveformComparisonConanairPage from './components/Pages/RawWaveformComparisonConanair/RawWaveformComparisonConanairPage';
import XYDiagramPage from './components/Pages/XYDiagram/XYDiagramPage';
import XYDiagramGroupSelectPage from './components/Pages/XYDiagram/XYDiagramGroupSelectPage';
import AlarmSummaryPage from './components/Pages/AlarmSummary/AlarmSummaryPage';
import AlarmHistoryPage from './components/Pages/AlarmHistory/AlarmHistoryPage';
import FFTComparisonPage from './components/Pages/FFTComparison/FFTComparisonPage';
import FFTComparisonGroupSelectPage from './components/Pages/FFTComparison/FFTComparisonGroupSelectPage';
import FFTComparisonConanairPage from './components/Pages/FFTComparisonConanair/FFTComparisonConanairPage';
import FFTComparisonConanairGroupSelectPage from './components/Pages/FFTComparisonConanair/FFTComparisonConanairGroupSelectPage';
import MeasureLogPage from './components/Pages/MeasureLog/MeasureLogPage';
import GroupSettingsPage from './components/Pages/GroupSettings/GroupSettingsPage';
import FeatureParameterPage from './components/Pages/FeatureParameter/FeatureParameterPage';
import MeasureSettingsPage from './components/Pages/MeasureSetting/MeasureSettingsPage';
import IoTDevListPage from './components/Pages/IoTDevList/IoTDevListPage';
import EMCTrendPage from './components/Pages/EMCTrend/EMCTrendPage';
import OffLineJudgePage from './components/Pages/OffLineJudge/OffLineJudgePage';
import PartialDischargeSettingPage from './components/Pages/PartialDischarge/PartialDischargeSettingPage';
import CustomConfigPage from './components/Pages/CustomConfig/CustomConfigPage';
import { BrowserRouter as Router, Route, Switch, Redirect } from 'react-router-dom';
import LoginPage from './components/Pages/Login/LoginPage';
import { LoginState } from './LoginState';
import { CSVDownload } from './components/molecules/CSVExport/CSVDownload';
import ReportPage from './components/Pages/Report/ReportPage';
import { useLocation } from 'react-router-dom';

/**
 * 簡易的なLangの型
 */
type LangInfo = {
  lang: string;
};

type ContextType = {
  state: StateType;
  dispatch: React.Dispatch<ActionType>;
  login: LoginState;
};

// コンテキストを作成する
export const AppContext = createContext({} as ContextType);

/**
 * デフォルトのLangを取得
 * ローカルストレージから取得できた場合はその値をパース
 * 取得できない場合はjaの情報を返す
 * @returns
 */
function getDefaultLangInfo(): LangInfo {
  const defaultLangInfo = window.localStorage.getItem('langInfo');
  if (defaultLangInfo) {
    console.debug(['getDefaultLangInfo localStorage ok', defaultLangInfo]);
    return JSON.parse(defaultLangInfo) as LangInfo;
  } else {
    console.debug(['getDefaultLangInfo localStorage ng']);
    return { lang: 'ja' };
  }
}

/**
 * Lang情報をローカルストレージに追加
 * @param lang
 */
function setLangInfoToLocalStorage(lang: string): void {
  const langInfoStringfy = JSON.stringify({ lang: lang });
  console.debug(['getDefaultLangInfo localStorage set ', langInfoStringfy]);
  window.localStorage.setItem('langInfo', langInfoStringfy);
}

const useTabCloseNotifier = () => {
  useEffect(() => {
    const handleUnload = () => {
      const data = JSON.stringify({ tabId: sessionStorage.getItem("tabId") });
      const blob = new Blob([data], { type: "application/json" });

      navigator.sendBeacon("/api/UserManage/PostTabCloseMode", blob);
    };

    window.addEventListener("beforeunload", handleUnload);
    return () => {
      window.removeEventListener("beforeunload", handleUnload);
    };
  }, []);
};

const App: React.FC = () => {
  const search = useLocation().search;
  const query = new URLSearchParams(search);
  console.debug(['paramsetting userid=', query.get('userid')]);
  const useridParam = query.get('userid') ?? '';

  const { i18n } = useTranslation();
  const [state, dispatch] = useReducer(appReducer, getDefaultLangInfo());
  const [loginFlg, setLoginFlg] = useState<boolean>(false);
  const login = LoginState.getInstance(setLoginFlg);

  useEffect(() => {
    console.debug(['useEffect', i18n, state.lang]);
    i18n.changeLanguage(state.lang);
    setLangInfoToLocalStorage(state.lang);
  }, [i18n, state.lang]);


  //タブが閉じられる際に実行
  useTabCloseNotifier();

  const loginStateFace = loginFlg ? (
    <Router>
      <AppHeader />
      <MenuTab />
      <Paper style={{ marginTop: 5, background: '#FFFFEE', whiteSpace: 'pre-wrap' }}>
        <Switch>
          {login.IsIoT ? null : <Route path="/Home" component={HomePage} exact />}
          {login.IsIoT ? <Route path="/Home" component={IoTDevListPage} exact /> : null}
          {login.IsIoT ? null : <Route path="/EMCTrendPage" component={EMCTrendPage} exact />}
          <Route path="/TrendGraphPage" component={TrendGraphPage} exact />
          <Route path="/TrendGraphGroupSelectPage" component={TrendGraphGroupSelectPage} exact />
          <Route path="/RealTimeTrendGraphPage" component={RealTimeTrendGraphPage} exact />
          <Route path="/RealTimeTrendGraphGroupSelectPage" component={RealTimeTrendGraphGroupSelectPage} exact />
          <Route path="/PhaseCurrentPage" component={PhaseCurrentPage} exact />
          <Route path="/PhaseCurrentGroupSelectPage" component={PhaseCurrentGroupSelectPage} exact />
          <Route path="/RawWaveformComparisonPage" component={RawWaveformComparisonPage} exact />
          <Route path="/RawWaveformComparisonGroupSelectPage" component={RawWaveformComparisonGroupSelectPage} exact />
          <Route path="/RawWaveformComparisonConanairPage" component={RawWaveformComparisonConanairPage} exact />
          <Route
            path="/RawWaveformComparisonConanairGroupSelectPage"
            component={RawWaveformComparisonConanairGroupSelectPage}
            exact
          />
          <Route path="/XYDiagramPage" component={XYDiagramPage} exact />
          <Route path="/XYDiagramGroupSelectPage" component={XYDiagramGroupSelectPage} exact />
          <Route path="/AlarmSummaryPage" component={AlarmSummaryPage} exact />
          <Route path="/AlarmHistoryPage" component={AlarmHistoryPage} exact />
          <Route path="/FFTComparisonPage" component={FFTComparisonPage} exact />
          <Route path="/FFTComparisonGroupSelectPage" component={FFTComparisonGroupSelectPage} exact />
          <Route path="/FFTComparisonConanairPage" component={FFTComparisonConanairPage} exact />
          <Route path="/FFTComparisonConanairGroupSelectPage" component={FFTComparisonConanairGroupSelectPage} exact />
          <Route path="/MeasureLogPage" component={MeasureLogPage} exact />
          <Route path="/GroupSettingsPage" component={GroupSettingsPage} exact />
          <Route path="/FeatureParameterPage" component={FeatureParameterPage} exact />
          <Route path="/TrendGraphPage" component={FeatureParameterPage} exact />
          <Route path="/MeasureSettingsPage" component={MeasureSettingsPage} exact />
          <Route path="/ReportPage" component={ReportPage} exact />
          <Route path="/OffLineJudgePage" component={OffLineJudgePage} exact />
          <Route path="/PartialDischargePage" component={PartialDischargeSettingPage} exact />
          <Route path="/CustomConfigPage" component={CustomConfigPage} exact />
          <Redirect to={{ pathname: '/Home' }} />
        </Switch>
      </Paper>
      <AppFooter />
      <CSVDownload />
    </Router>
  ) : (
    <Router>
      <Route path="/" render={(props) => <LoginPage userid={useridParam} {...props} />} exact />
      <Redirect to={{ pathname: '/' }} />
    </Router>
  );

  return <AppContext.Provider value={{ state, dispatch, login }}>{loginStateFace}</AppContext.Provider>;
};

export default App;
